import { z } from 'zod';

export {
  ctaCornerStyleValues,
  contrastRatio,
  ctaThemeCssVariables,
  DEFAULT_CTA_THEME,
  mixHexColors,
  normalizeCtaTheme,
  normalizeHexColor,
  readableTextColor,
  relativeLuminance,
  resolveCtaTheme
} from './ctaTheme';
export type { CtaCornerStyle, CtaTheme } from './ctaTheme';
export {
  DEFAULT_SCAN_PAGE_CONFIG,
  isScanPageConfigured,
  normalizeScanPageConfig,
  scanPageButtonStyleValues,
  scanPageConfigCssVariables,
  scanPageCrosshairValues,
  scanPageFontValues
} from './scanPageConfig';
export type {
  ScanPageButtonStyle,
  ScanPageConfig,
  ScanPageCrosshair,
  ScanPageFont
} from './scanPageConfig';

export const PRODUCT_NAME = 'ADarveo';
export const MARKETING_DOMAIN = 'adarveo.com';
export const DASHBOARD_DOMAIN = 'app.adarveo.com';
export const API_DOMAIN = 'api.adarveo.com';
export const PUBLIC_SCAN_BASE_URL = 'https://adarveo.com/a';
export const PUBLIC_COLLECTION_SCAN_BASE_URL = 'https://adarveo.com/m';
export const PUBLIC_TARGET_BASE = 'https://minds.adarveo.com';

export const statusValues = ['draft', 'processing', 'active', 'paused', 'ended', 'failed'] as const;
export const roleValues = ['owner', 'admin', 'member', 'viewer'] as const;
export const assetTypeValues = ['image', 'video', 'marker', 'mind'] as const;
export const assetProviderValues = [
  'cloudflare_images',
  'cloudflare_stream',
  'cloudflare_r2',
  'supabase'
] as const;
export const eventTypeValues = [
  'qr_scan',
  'landing_view',
  'viewer_loaded',
  'camera_opened',
  'camera_permission_denied',
  'image_recognized',
  'ar_started',
  'video_started',
  'video_progress',
  'video_completed',
  'cta_impression',
  'cta_clicked',
  'target_visible_duration',
  'mind_compile_requested',
  'mind_compile_completed',
  'collection_compile_requested',
  'collection_compile_completed',
  'error'
] as const;

export type CampaignStatus = (typeof statusValues)[number];
export type OrganizationRole = (typeof roleValues)[number];
export type AssetType = (typeof assetTypeValues)[number];
export type AssetProvider = (typeof assetProviderValues)[number];
export type AnalyticsEventType = (typeof eventTypeValues)[number];

export const analyticsEventSchema = z.object({
  publicSlug: z.string().min(3).max(120),
  eventType: z.enum(eventTypeValues),
  sessionId: z.string().min(4).max(120).optional(),
  visitorId: z.string().min(4).max(120).optional(),
  metadata: z.record(z.unknown()).optional()
});

export const compileRequestSchema = z.object({
  adId: z.string().uuid(),
  markerImageUrl: z.string().url().optional(),
  force: z.boolean().optional().default(false)
});

export const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

export const buildPublicScanUrl = (publicSlug: string, baseUrl = PUBLIC_SCAN_BASE_URL) =>
  `${baseUrl.replace(/\/$/, '')}/${encodeURIComponent(publicSlug)}`;

export const buildQrScanUrl = (publicSlug: string, baseUrl = PUBLIC_SCAN_BASE_URL) => {
  const url = new URL(buildPublicScanUrl(publicSlug, baseUrl));
  url.searchParams.set('source', 'qr');
  return url.toString();
};

export const buildPublicCollectionScanUrl = (collectionSlug: string, baseUrl = PUBLIC_COLLECTION_SCAN_BASE_URL) =>
  `${baseUrl.replace(/\/$/, '')}/${encodeURIComponent(collectionSlug)}`;

export const buildCollectionQrScanUrl = (collectionSlug: string, baseUrl = PUBLIC_COLLECTION_SCAN_BASE_URL) => {
  const url = new URL(buildPublicCollectionScanUrl(collectionSlug, baseUrl));
  url.searchParams.set('source', 'qr');
  return url.toString();
};

export const formatCompactNumber = (value: number) =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
